// report-estudantes.component.ts
import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';

import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { Toast } from 'primeng/toast';
import { Dialog } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';

import { ApiService } from '@core/providers/http';
import { Pagination } from '@app/shared/model';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import {
  TableComponent,
  TableColumn,
} from '@app/shared/components/table/table.component';
import { AuthStore } from '@core/service/auth-store.service';

// Services (ajusta caminhos conforme a tua estrutura)
import {
  BolseiroService,
  BolsaService,
  InstituicaoEnsinoCursoService,
  InstituicaoEnsinoService,
  CursoService,
  InstituicaoGestoraService,
  PaisService,
  AreaFormacaoService,
  EnumService,
  EstudantesReportService,
  ReportFilters, // <- interface dos filtros no service
} from '@app/features-modules/gestao-bolseiros/components/api/services';

@Component({
  selector: 'app-report-estudantes',
  standalone: true,
  providers: [
    MessageService,
    ApiService,
    EstudantesReportService,
    BolsaService,
    BolseiroService,
    InstituicaoEnsinoService,
    InstituicaoEnsinoCursoService,
    CursoService,
    InstituicaoGestoraService,
    PaisService,
    AreaFormacaoService,
    EnumService,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    // UI
    DataVoidComponent,
    ButtonModule,
    InputNumberModule,
    Dialog,
    SelectModule,
    TableModule,
    DatePickerModule,
    Toast,
    TableComponent,
  ],
  templateUrl: './report-estudantes.component.html',
  styleUrls: ['./report-estudantes.component.scss'],
})
export class ReportEstudantesComponent implements OnInit {
  // Injeções
  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private auth = inject(AuthStore);
  private enumService = inject(EnumService);
  private instituicaoEnsinoService = inject(InstituicaoEnsinoService);
  private cursoService = inject(CursoService);
  private instituicaoGestoraService = inject(InstituicaoGestoraService);
  private areaFormacaoService = inject(AreaFormacaoService);
  private paisService = inject(PaisService);
  private estudantesReportService = inject(EstudantesReportService);

  // Estado
  formEstudantes!: FormGroup;
  estudantes: any[] = [];
  pagination: Pagination<any> = new Pagination<any>({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
    data: [],
  });
  totalBolseiros = 0;
  showModalEstudantes = false;
  carregando = false;

  appliedFilters: { label: string; value: string }[] = [];

  // Dropdowns
  cursoList: { label: string; value: number }[] = [];
  areaFormacaoList: { label: string; value: number }[] = [];
  paisList: { label: string; value: number }[] = [];
  instituicaoEnsinoList: { label: string; value: number }[] = [];
  instituicaoGestoraList: { label: string; value: number }[] = [];
  nivelAcademicoOptions: { id: number; label: string }[] = [];
  generoOptions: { id: number; label: string }[] = [];
  estadoBolsaOptions: { id: number; label: string }[] = [];
  anoCurricularOptions = Array.from({ length: 10 }, (_, i) => {
    const ano = i + 1;
    return { label: ano.toString(), value: ano };
  });

  // “Tipo de relatório” (mantive a tua chave titlleOptions para não partir nada)
  titlleOptions = [
    { label: 'Relatório de Estudantes Finalistas', value: 1 },
    { label: 'Relatório de Estudantes Matriculados', value: 2 },
    { label: 'Relatório de Estudantes Enquadrados', value: 3 },
    { label: 'Relatório de Estudantes Activos', value: 4 },
    {
      label: 'Relatório de Estudantes Activos Distribuídos por Curso',
      value: 5,
    },
    { label: 'Relatório de Estudantes Diplomados', value: 6 },
  ];

  anoInscricaoOptions = Array.from({ length: 10 }, (_, i) => {
    const ano = new Date().getFullYear() - i;
    return { label: ano.toString(), value: ano };
  });

  columns: TableColumn[] = [
    { field: 'estudante', header: 'Estudante', type: 'text' },
    { field: 'generoDescricao', header: 'Género', type: 'text' },
    { field: 'idade', header: 'Idade', type: 'text' },
    {
      field: 'nivelAcademicoDescricao',
      header: 'Nível Académico',
      type: 'text',
    },
    {
      field: 'areaDeConhecimento',
      header: 'Área de Conhecimento',
      type: 'text',
    },
    { field: 'curso', header: 'Curso', type: 'text' },
    { field: 'instituicao', header: 'Instituição de Ensino', type: 'text' },
    { field: 'pais', header: 'País de Estudo', type: 'text' },
    { field: 'estadoBolsaDescricao', header: 'Estado da Bolsa', type: 'text' },
  ];

  // Ciclo de vida
  ngOnInit() {
    if (this.auth.hasAny(['emitir_relatório_estudante'])) {
      this.buildForm();
      this.loadDropdowns();
      this.showModalEstudantes = false;
      this.loadData();
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Permissão Inválida',
        detail: 'Não tens permissão para efetuar esta acção',
        life: 3000,
      });
    }
  }

  // Form
  private buildForm() {
    this.formEstudantes = this.fb.group({
      anoInscricao: [null],
      nivelAcademico: [null],
      areaConhecimentoId: [null],
      anoCurricular: [null],
      cursoId: [null],
      inicioFormacao: [null],
      fimFormacao: [null],
      instituicaoId: [null],
      paisId: [null],
      instituicaoGestoraId: [null],
      genero: [null],
      idadeMinima: [null],
      idadeMaxima: [null],
      estadoBolsaId: [null],
      titulo: ['Relatório de Estudantes Activos', Validators.required],
    });
  }

  // Dropdowns
  loadDropdowns() {
    forkJoin({
      estadoBolsa: this.enumService.getAllEstadoBolsaEnum(),
      nivelAcademico: this.enumService.getAllNivelAcademicoMIREMPETEnum(),
      genero: this.enumService.getAllGeneroEnum(),
      instituicoesEnsino: this.instituicaoEnsinoService.list({ pageSize: 100 }),
      cursos: this.cursoService.list({ pageSize: 100 }),
      instituicoesGestora: this.instituicaoGestoraService.list({
        pageSize: 100,
      }),
      paises: this.paisService.list({ pageSize: 100 }),
      areaFormacao: this.areaFormacaoService.list({ pageSize: 100 }),
    }).subscribe((response) => {
      // Enums
      if (response.estadoBolsa?.data) {
        this.estadoBolsaOptions = Object.entries(response.estadoBolsa.data).map(
          ([key, value]) => ({
            id: Number(key),
            label: value as string,
          }),
        );
      }
      if (response.nivelAcademico?.data) {
        this.nivelAcademicoOptions = Object.entries(
          response.nivelAcademico.data,
        ).map(([key, value]) => ({
          id: Number(key),
          label: value as string,
        }));
      }
      if (response.genero?.data) {
        this.generoOptions = Object.entries(response.genero.data).map(
          ([key, value]) => ({
            id: Number(key),
            label: value as string,
          }),
        );
      }

      // Listas
      if (response.instituicoesEnsino?.data) {
        this.instituicaoEnsinoList = response.instituicoesEnsino.data.map(
          (item: any) => ({
            label: item.nome,
            value: item.id,
          }),
        );
      }
      if (response.cursos?.data) {
        this.cursoList = response.cursos.data.map((item: any) => ({
          label: item.designacao,
          value: item.id,
        }));
      }
      if (response.instituicoesGestora?.data) {
        this.instituicaoGestoraList = response.instituicoesGestora.data.map(
          (item: any) => ({
            label: item.nome,
            value: item.id,
          }),
        );
      }
      if (response.paises || Array.isArray(response.paises)) {
        const paisesArr = Array.isArray(response.paises) ? response.paises : [];
        this.paisList = paisesArr.map((item: any) => ({
          label: item.designacao,
          value: item.id,
        }));
      }
      if (response.areaFormacao?.data) {
        this.areaFormacaoList = response.areaFormacao.data.map((item: any) => ({
          label: item.designacao,
          value: item.id,
        }));
      }
    });
  }

  // Helpers
  private normalizeDate(d: any): string | null {
    if (!d) return null;
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private buildFilters(extra?: Partial<ReportFilters>): ReportFilters {
    const v = this.formEstudantes.getRawValue();
    const filters: ReportFilters = {
      titulo: v.titulo, // manter se o backend usa para selecionar o tipo
      anoInscricao: v.anoInscricao,
      nivelAcademico: v.nivelAcademico,
      areaConhecimentoId: v.areaConhecimentoId,
      cursoId: v.cursoId,
      inicioFormacao: this.normalizeDate(v.inicioFormacao),
      fimFormacao: this.normalizeDate(v.fimFormacao),
      instituicaoId: v.instituicaoId,
      paisId: v.paisId,
      instituicaoGestoraId: v.instituicaoGestoraId,
      genero: v.genero,
      idadeMinima: v.idadeMinima,
      idadeMaxima: v.idadeMaxima,
      estadoBolsaId: v.estadoBolsaId,
      anoCurricular: v.anoCurricular,
      ...extra,
    };
    return filters;
  }

  // UI: filtros aplicados (badge)
  updateAppliedFilters() {
    const formValue = this.formEstudantes.value;
    const filters: { label: string; value: string }[] = [];

    if (formValue.anoInscricao) {
      filters.push({
        label: 'Ano de Inscrição',
        value: String(formValue.anoInscricao),
      });
    }
    if (formValue.nivelAcademico) {
      const nivel = this.nivelAcademicoOptions.find(
        (n) => n.id === formValue.nivelAcademico,
      )?.label;
      filters.push({
        label: 'Nível Académico',
        value: nivel || String(formValue.nivelAcademico),
      });
    }
    if (formValue.areaConhecimentoId) {
      const area = this.areaFormacaoList.find(
        (a) => a.value === formValue.areaConhecimentoId,
      )?.label;
      filters.push({
        label: 'Área de Conhecimento',
        value: area || String(formValue.areaConhecimentoId),
      });
    }
    if (formValue.cursoId) {
      const curso = this.cursoList.find(
        (c) => c.value === formValue.cursoId,
      )?.label;
      filters.push({
        label: 'Curso',
        value: curso || String(formValue.cursoId),
      });
    }
    if (formValue.instituicaoId) {
      const inst = this.instituicaoEnsinoList.find(
        (i) => i.value === formValue.instituicaoId,
      )?.label;
      filters.push({
        label: 'Instituição de Ensino',
        value: inst || String(formValue.instituicaoId),
      });
    }
    if (formValue.paisId) {
      const pais = this.paisList.find(
        (p) => p.value === formValue.paisId,
      )?.label;
      filters.push({
        label: 'País de Estudo',
        value: pais || String(formValue.paisId),
      });
    }
    if (formValue.instituicaoGestoraId) {
      const gest = this.instituicaoGestoraList.find(
        (g) => g.value === formValue.instituicaoGestoraId,
      )?.label;
      filters.push({
        label: 'Entidade Gestora',
        value: gest || String(formValue.instituicaoGestoraId),
      });
    }
    if (formValue.genero) {
      const genero = this.generoOptions.find(
        (g) => g.id === formValue.genero,
      )?.label;
      filters.push({
        label: 'Género',
        value: genero || String(formValue.genero),
      });
    }
    if (formValue.idadeMaxima) {
      filters.push({
        label: 'Idade Máxima',
        value: String(formValue.idadeMaxima),
      });
    }
    if (formValue.idadeMinima) {
      filters.push({
        label: 'Idade Mínima',
        value: String(formValue.idadeMinima),
      });
    }
    if (formValue.estadoBolsaId) {
      const estado = this.estadoBolsaOptions.find(
        (e) => e.id === formValue.estadoBolsaId,
      )?.label;
      filters.push({
        label: 'Estado da Bolsa',
        value: estado || String(formValue.estadoBolsaId),
      });
    }

    if (formValue.anoCurricular) {
      filters.push({
        label: 'Ano Curricular',
        value: String(formValue.anoCurricular),
      });
    }
    this.appliedFilters = filters;
  }

  // Ações
  onSubmit() {
    this.formEstudantes.markAllAsTouched();
    if (this.formEstudantes.valid) {
      this.pagination.pageNumber = 1;
      this.loadData();
      this.updateAppliedFilters();
      this.showModalEstudantes = false;
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Por favor, preencha os campos obrigatórios do formulário.',
        life: 3000,
      });
    }
  }

  limparFiltrosEstudantes() {
    this.formEstudantes.reset({
      titulo: 'Relatório de Estudantes',
      anoInscricao: null,
      nivelAcademico: null,
      areaConhecimentoId: null,
      cursoId: null,
      inicioFormacao: null,
      fimFormacao: null,
      instituicaoId: null,
      paisId: null,
      instituicaoGestoraId: null,
      genero: null,
      idadeMinima: null,
      idadeMaxima: null,
      estadoBolsaId: null,
      anoCurricular: null,
    });
    this.appliedFilters = [];
    this.pagination.pageNumber = 1;
    this.loadData();
    this.showModalEstudantes = false;
    this.messageService.add({
      severity: 'info',
      summary: 'Filtros Reiniciados',
      detail: 'Parâmetros de pesquisa restaurados para o padrão.',
      life: 2500,
    });
  }

  emitirPdfComFiltros() {
    this.formEstudantes.markAllAsTouched();
    if (this.formEstudantes.valid) {
      this.downloadPdf();
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha os campos obrigatórios para exportar o PDF.',
        life: 3000,
      });
    }
  }

  emitirExcelComFiltros() {
    this.formEstudantes.markAllAsTouched();
    if (this.formEstudantes.valid) {
      this.downloadExcel();
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha os campos obrigatórios para exportar o Excel.',
        life: 3000,
      });
    }
  }

  loadData(queryParams?: any) {
    this.carregando = true;
    const filters = this.buildFilters({
      pageNumber: this.pagination.pageNumber ?? 1,
      pageSize: this.pagination.pageSize ?? 10,
      OrderBy: queryParams?.OrderBy || 'idade desc',
    });

    console.log('Params enviados para filtrar estudantes:', filters);
    this.estudantesReportService.filtrarEstudantes(filters).subscribe({
      next: (res) => {
        this.carregando = false;
        this.pagination = new Pagination<any>().deserialize(res);
        const total = res.totalRecords ?? this.pagination.data?.length ?? 0;
        this.pagination.totalRecords = total;
        this.pagination.pageSize = res.pageSize ?? this.pagination.pageSize ?? 10;
        this.pagination.pageNumber = res.pageNumber ?? this.pagination.pageNumber ?? 1;
        this.pagination.totalPages =
          res.totalPages ?? Math.ceil(total / (this.pagination.pageSize || 10));
        this.estudantes = res.data || [];
        this.totalBolseiros = total;

        if (total > 0) {
          this.messageService.add({
            severity: 'success',
            summary: 'Relatório Emitido',
            detail: `${total} estudante(s) encontrado(s).`,
            life: 3000,
          });
        } else {
          this.messageService.add({
            severity: 'info',
            summary: 'Pesquisa Concluída',
            detail: 'Nenhum estudante encontrado com os filtros informados.',
            life: 3500,
          });
        }
      },
      error: (err) => {
        this.carregando = false;
        this.totalBolseiros = 0;
        this.estudantes = [];
        console.error('Erro ao consultar estudantes:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro na Consulta',
          detail: 'Não foi possível carregar os dados dos estudantes. Verifique os filtros ou tente novamente.',
          life: 4000,
        });
      },
    });
  }

  onPageChange(event: any) {
    const page =
      event.page !== undefined
        ? event.page + 1
        : Math.floor((event.first ?? 0) / (event.rows ?? this.pagination.pageSize ?? 10)) + 1;
    this.pagination.pageNumber = page;
    this.pagination.pageSize = event.rows ?? this.pagination.pageSize ?? 10;
    this.loadData();
  }

  downloadPdf() {
    const filters = this.buildFilters(); // apenas filtros
    this.estudantesReportService.downloadPdfByFilters(filters).subscribe(
      (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Relatorio-Estudantes.pdf';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao exportar relatório.',
          life: 3000,
        });
      },
    );
  }

  downloadExcel() {
    const filters = this.buildFilters(); // apenas filtros
    this.estudantesReportService.downloadExcelByFilters(filters).subscribe(
      (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Relatorio-Estudantes.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao exportar relatório.',
          life: 3000,
        });
      },
    );
  }
}
